import React, { useEffect, useState } from 'react';
import { COLORS } from '../../../core/constants/colors';
import { recommendationsService } from '../services/recommendations.service';
import { CompanyRecommendation, QueryContextRequest } from '../types';

interface QueryRecommendationsSectionProps {
  queries: string[]; // Recent queries to base recommendations on
  onCompanyClick?: (companyId: number) => void;
  onDismiss?: () => void;
}

export const QueryRecommendationsSection: React.FC<QueryRecommendationsSectionProps> = ({
  queries,
  onCompanyClick,
  onDismiss,
}) => {
  const [recommendations, setRecommendations] = useState<CompanyRecommendation[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedCard, setExpandedCard] = useState<number | null>(null);

  useEffect(() => {
    loadRecommendations();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queries]);

  const loadRecommendations = async () => {
    if (queries.length === 0) return;

    setIsLoading(true);
    setError(null);
    try {
      // Build query context from recent queries
      const queryContext: QueryContextRequest = {
        description: queries.join('. '),
        limit: 5,
      };

      const data = await recommendationsService.getQueryContextRecommendations(queryContext);
      setRecommendations(data.recommendations);
    } catch (err: any) {
      setError(err.message || 'Failed to load recommendations');
    } finally {
      setIsLoading(false);
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 0.8) return { bg: '#D1FAE5', text: '#059669' };
    if (score >= 0.6) return { bg: '#E3F2FD', text: '#1565C0' };
    if (score >= 0.4) return { bg: '#FFF3E0', text: '#E65100' };
    return { bg: '#F3F4F6', text: '#6B7280' };
  };

  const getRecommendationTypeBadge = (type: string) => {
    switch (type.toLowerCase()) {
      case 'complementary':
        return { bg: '#E3F2FD', text: '#1565C0', icon: '🤝', label: 'Complementary' };
      case 'partnership':
        return { bg: '#F3E5F5', text: '#7B1FA2', icon: '🔗', label: 'Partnership' };
      case 'similar':
        return { bg: '#E8F5E9', text: '#2E7D32', icon: '🎯', label: 'Similar' };
      default:
        return { bg: '#F5F5F5', text: '#616161', icon: '✨', label: 'Recommended' };
    }
  };

  if (isLoading) {
    return (
      <div className="my-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 shadow-sm border border-blue-100">
        <div className="flex items-center justify-center py-4">
          <div
            className="inline-block animate-spin rounded-full h-6 w-6 border-4 border-t-transparent mr-3"
            style={{ borderColor: COLORS.user.primary, borderTopColor: 'transparent' }}
          />
          <span className="text-sm text-gray-600">Finding relevant companies...</span>
        </div>
      </div>
    );
  }

  if (error || recommendations.length === 0) {
    return null; // Don't show if there's an error or no recommendations
  }

  return (
    <div className="my-6 bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 shadow-sm border border-blue-100 animate-fadeIn">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: COLORS.user.primary }}
          >
            <svg
              className="w-5 h-5 text-white"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-bold" style={{ color: '#1A1A1A' }}>
              💡 Recommended Companies
            </h3>
            <p className="text-xs text-gray-600 mt-0.5">
              Based on your recent queries, these companies might interest you
            </p>
          </div>
        </div>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      <div className="space-y-4">
        {recommendations.map((rec, idx) => {
          const scoreColors = getScoreColor(rec.combined_score);
          const typeBadge = getRecommendationTypeBadge(rec.recommendation_type);
          const isExpanded = expandedCard === rec.company_id;
          
          return (
            <div
              key={rec.company_id}
              className="bg-white rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-lg transition-all overflow-hidden"
              style={{ boxShadow: isExpanded ? '0 10px 25px rgba(0, 0, 0, 0.1)' : undefined }}
            >
              {/* Main Card Content */}
              <div className="p-5">
                {/* Header Row */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold text-white"
                      style={{ backgroundColor: COLORS.user.primary }}
                    >
                      #{idx + 1}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 
                          className="font-bold text-base cursor-pointer hover:underline"
                          style={{ color: '#1A1A1A' }}
                          onClick={() => onCompanyClick?.(rec.company_id)}
                        >
                          {rec.company_name}
                        </h4>
                        <span
                          className="px-2 py-1 rounded-full text-xs font-semibold"
                          style={{ backgroundColor: scoreColors.bg, color: scoreColors.text }}
                        >
                          {(rec.combined_score * 100).toFixed(0)}%
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span
                          className="px-2 py-0.5 rounded-md text-xs font-medium"
                          style={{ backgroundColor: typeBadge.bg, color: typeBadge.text }}
                        >
                          {typeBadge.icon} {typeBadge.label}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                {rec.description && (
                  <p className="text-sm text-gray-700 mb-3 leading-relaxed">
                    {rec.description}
                  </p>
                )}

                {/* Meta Info */}
                <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                  {rec.industry && (
                    <span className="flex items-center gap-1.5 bg-gray-50 px-2 py-1 rounded-md">
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
                      </svg>
                      {rec.industry}
                    </span>
                  )}
                  {rec.location && (
                    <span className="flex items-center gap-1.5 bg-gray-50 px-2 py-1 rounded-md">
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                      </svg>
                      {rec.location}
                    </span>
                  )}
                  {rec.website && (
                    <a
                      href={rec.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="flex items-center gap-1.5 bg-blue-50 px-2 py-1 rounded-md text-blue-600 hover:bg-blue-100 transition-colors"
                    >
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
                      </svg>
                      Visit
                    </a>
                  )}
                </div>

                {/* Match Scores Bar */}
                <div className="mb-3 p-3 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg">
                  <div className="flex items-center justify-between text-xs mb-2">
                    <span className="font-medium text-gray-700">Compatibility Breakdown</span>
                    <span className="text-gray-500">
                      Vector: {(rec.vector_similarity_score * 100).toFixed(0)}% • 
                      Metadata: {(rec.metadata_match_score * 100).toFixed(0)}%
                    </span>
                  </div>
                  <div className="grid grid-cols-5 gap-2">
                    {[
                      { label: 'Industry', value: rec.metadata_details.industry_match },
                      { label: 'Market', value: rec.metadata_details.target_market_match },
                      { label: 'Tech', value: rec.metadata_details.technology_match },
                      { label: 'Location', value: rec.metadata_details.location_match },
                      { label: 'Size', value: rec.metadata_details.size_match },
                    ].map((item) => (
                      <div key={item.label} className="text-center">
                        <div className="text-xs font-semibold mb-1" style={{ color: COLORS.user.primary }}>
                          {(item.value * 100).toFixed(0)}%
                        </div>
                        <div className="text-xs text-gray-500">{item.label}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Alignment Summary */}
                {rec.insights && (
                  <div className="bg-amber-50 border-l-4 border-amber-400 p-3 rounded-r-lg mb-3">
                    <p className="text-xs text-gray-700 leading-relaxed">
                      <span className="font-semibold text-amber-900">💡 Strategic Alignment:</span>{' '}
                      {rec.insights.alignment_summary}
                    </p>
                  </div>
                )}

                {/* Expand Button */}
                {rec.insights && rec.insights.synergy_areas.length > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setExpandedCard(isExpanded ? null : rec.company_id);
                    }}
                    className="w-full flex items-center justify-center gap-2 py-2 text-sm font-medium rounded-lg transition-colors"
                    style={{
                      color: COLORS.user.primary,
                      backgroundColor: 'rgba(99, 102, 241, 0.05)',
                    }}
                  >
                    {isExpanded ? (
                      <>
                        <span>Hide Details</span>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                        </svg>
                      </>
                    ) : (
                      <>
                        <span>View Synergy Areas & Partnership Benefits</span>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </>
                    )}
                  </button>
                )}
              </div>

              {/* Expanded Section */}
              {isExpanded && rec.insights && (
                <div className="border-t bg-gradient-to-b from-white to-gray-50 p-5 space-y-4 animate-slideDown">
                  {/* Synergy Areas */}
                  {rec.insights.synergy_areas.length > 0 && (
                    <div>
                      <h5 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: '#1A1A1A' }}>
                        <span className="text-lg">🎯</span>
                        Key Synergy Opportunities
                      </h5>
                      <div className="space-y-2">
                        {rec.insights.synergy_areas.map((area, i) => (
                          <div key={i} className="flex items-start gap-3 bg-white p-3 rounded-lg border border-gray-100">
                            <div
                              className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold text-white"
                              style={{ backgroundColor: COLORS.user.queryButton }}
                            >
                              {i + 1}
                            </div>
                            <p className="text-sm text-gray-700 leading-relaxed">{area}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Partnership Rationale */}
                  <div>
                    <h5 className="text-sm font-bold mb-2 flex items-center gap-2" style={{ color: '#1A1A1A' }}>
                      <span className="text-lg">🤝</span>
                      Why Partner?
                    </h5>
                    <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                      <p className="text-sm text-gray-700 leading-relaxed">
                        {rec.insights.partnership_rationale}
                      </p>
                    </div>
                  </div>

                  {/* CTA */}
                  <button
                    onClick={() => onCompanyClick?.(rec.company_id)}
                    className="w-full py-3 rounded-xl font-semibold text-white transition-all hover:shadow-lg"
                    style={{ backgroundColor: COLORS.user.queryButton }}
                  >
                    View {rec.company_name} Profile →
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

